import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { axiosInstance } from '../lib/axios';
import { toast } from 'react-toastify';

// Interface for form data
interface DemoRequestForm {
    name: string;
    phone: string;
    city: string;
    restaurantName: string;
    email: string;
}

interface FreeDemoRequestPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const FreeDemoRequestPopup: React.FC<FreeDemoRequestPopupProps> = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState<DemoRequestForm>({
        name: '',
        phone: '',
        city: '',
        restaurantName: '',
        email: '',
    });

    const [errors, setErrors] = useState<Partial<DemoRequestForm>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValid, setIsValid] = useState(false);

    // Validation Logic
    useEffect(() => {
        const validate = () => {
            const newErrors: Partial<DemoRequestForm> = {};
            let valid = true;

            // Basic emptiness check is handled by "mandatoryFilled" logic mostly, 
            // but specific regex validation needs to be explicit.

            // Phone validation: exactly 10 digits
            const phoneRegex = /^\d{10}$/;
            if (formData.phone && !phoneRegex.test(formData.phone)) {
                newErrors.phone = 'Phone number must be exactly 10 digits';
                valid = false;
            }

            // Email validation
            if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                newErrors.email = 'Valid email is required';
                valid = false;
            }

            // Check mandatory fields for "Submit button disabled"
            const mandatoryFilled = !!(
                formData.name.trim() &&
                formData.phone.trim() &&
                formData.city.trim() &&
                formData.restaurantName.trim() &&
                formData.email.trim()
            );

            setErrors(newErrors);
            setIsValid(valid && mandatoryFilled);
        };

        validate();
    }, [formData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // For phone, only allow numeric input
        if (name === 'phone') {
            if (/^\d*$/.test(value) && value.length <= 10) {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) return;

        setIsSubmitting(true);
        try {
            // POST /api/demo-requests (public endpoint for unauthenticated visitors)
            await axiosInstance.post('/api/demo-requests', {
                ...formData,
                source: 'Website Demo Popup'
            });

            toast.success('Thank you! Our team will contact you shortly.');
            onSuccess(); // Triggers "NEVER show again" logic
            onClose();
        } catch (error) {
            console.error('Submission failed', error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={undefined} // Interaction disabled as per requirements
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                    >
                        {/* Header with Close */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white shrink-0">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">Get Your Free Demo</h2>
                                <p className="text-sm text-gray-500 mt-1">See how Willovate can transform your restaurant</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                aria-label="Close"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 overflow-y-auto">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name*</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="John Doe"
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                                    />
                                </div>

                                {/* Phone & City Row */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number*</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2 text-gray-500">+91</span>
                                            <input
                                                type="text"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="9876543210"
                                                className={`w-full pl-12 pr-4 py-2 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none`}
                                            />
                                        </div>
                                        {errors.phone ? (
                                            <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                                        ) : (
                                            <p className="text-xs text-gray-400 mt-1">Exactly 10 digits</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">City*</label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            placeholder="Mumbai"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                                        />
                                    </div>
                                </div>

                                {/* Restaurant Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Name*</label>
                                    <input
                                        type="text"
                                        name="restaurantName"
                                        value={formData.restaurantName}
                                        onChange={handleChange}
                                        placeholder="Tasty Bites"
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                                    />
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="john@example.com"
                                        className={`w-full px-4 py-2 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none`}
                                    />
                                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                                </div>

                                {/* Submit */}
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={!isValid || isSubmitting}
                                        className={`w-full py-3 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-95
                                    ${isValid && !isSubmitting
                                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow-blue-500/30 hover:brightness-110'
                                                : 'bg-gray-300 cursor-not-allowed'}
                                `}
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Submitting...
                                            </span>
                                        ) : 'Submit'}
                                    </button>
                                    <p className="text-center text-xs text-gray-400 mt-3">
                                        We respect your privacy. No spam guaranteed.
                                    </p>
                                </div>

                            </form>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default FreeDemoRequestPopup;
